import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Tag,
    Space,
    Modal,
    Input,
    message,
    Card,
    Typography,
    Badge,
    Tabs,
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { workHourAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const WorkLogApprovalPage = () => {
    const [pendingLogs, setPendingLogs] = useState([]);
    const [approvedLogs, setApprovedLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [activeTab, setActiveTab] = useState('pending');

    useEffect(() => {
        fetchWorkLogs();
    }, [activeTab]);

    const fetchWorkLogs = async () => {
        setLoading(true);
        try {
            const status = activeTab === 'pending' ? 'pending' : 'approved';
            const response = await workHourAPI.getForApproval({ status });

            if (activeTab === 'pending') {
                setPendingLogs(response.data.work_logs);
            } else {
                setApprovedLogs(response.data.work_logs);
            }
        } catch (error) {
            message.error('작업 일지 조회 실패');
            console.error('Fetch work logs error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = (record) => {
        confirm({
            title: '작업 일지 승인',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>작업자: {record.user?.name}</p>
                    <p>작업일: {dayjs(record.work_date).format('YYYY-MM-DD')}</p>
                    <p>작업시간: {record.hours}시간</p>
                    <p>인건비: {parseInt(record.labor_cost || 0).toLocaleString()}원</p>
                    <br />
                    <Text strong>승인하시겠습니까?</Text>
                </div>
            ),
            okText: '승인',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await workHourAPI.approve(record.id, { action: 'approve' });
                    message.success('작업 일지가 승인되었습니다');
                    fetchWorkLogs();
                } catch (error) {
                    message.error(error.response?.data?.message || '승인 처리 실패');
                }
            },
        });
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            message.error('반려 사유를 입력해주세요');
            return;
        }

        try {
            await workHourAPI.approve(selectedLog.id, {
                action: 'reject',
                rejection_reason: rejectionReason,
            });
            message.success('작업 일지가 반려되었습니다');
            setRejectModalVisible(false);
            setRejectionReason('');
            setSelectedLog(null);
            fetchWorkLogs();
        } catch (error) {
            message.error(error.response?.data?.message || '반려 처리 실패');
        }
    };

    const workTypeMap = {
        development: '개발',
        design: '설계',
        meeting: '회의',
        research: '조사/연구',
        documentation: '문서작업',
        support: '지원',
        other: '기타',
    };

    const columns = [
        {
            title: '작업일',
            dataIndex: 'work_date',
            key: 'work_date',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            ellipsis: true,
        },
        {
            title: '작업자',
            dataIndex: ['user', 'name'],
            key: 'user',
            width: 100,
        },
        {
            title: '작업시간',
            dataIndex: 'hours',
            key: 'hours',
            width: 100,
            align: 'center',
            render: (hours) => <Tag color="blue">{Number(hours).toFixed(1)}h</Tag>,
        },
        {
            title: '인건비',
            dataIndex: 'labor_cost',
            key: 'labor_cost',
            width: 120,
            align: 'right',
            render: (cost) => `${parseInt(cost || 0).toLocaleString()}원`,
        },
        {
            title: '작업내용',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
    ];

    // 승인 대기 컬럼 (작업 버튼 포함)
    const pendingColumns = [
        ...columns,
        {
            title: '작업',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => handleApprove(record)}
                    >
                        승인
                    </Button>
                    <Button
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => {
                            setSelectedLog(record);
                            setRejectModalVisible(true);
                        }}
                    >
                        반려
                    </Button>
                </Space>
            ),
        },
    ];

    // 승인 완료 컬럼 (승인일 추가)
    const approvedColumns = [
        ...columns,
        {
            title: '승인일',
            dataIndex: 'approved_at',
            key: 'approved_at',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '승인자',
            dataIndex: ['approver', 'name'],
            key: 'approver',
            width: 100,
        },
    ];

    return (
        <div>
            <Title level={2}>작업 일지 승인 (PM)</Title>

            <Card style={{ marginTop: 24 }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'pending',
                            label: (
                                <span>
                  승인 대기
                  <Badge
                      count={pendingLogs.length}
                      style={{ marginLeft: 8, backgroundColor: '#faad14' }}
                  />
                </span>
                            ),
                            children: (
                                <Table
                                    columns={pendingColumns}
                                    dataSource={pendingLogs}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{ pageSize: 10 }}
                                    scroll={{ x: 1200 }}
                                    locale={{ emptyText: '승인 대기 중인 작업 일지가 없습니다' }}
                                    expandable={{
                                        expandedRowRender: (record) => (
                                            <div style={{ padding: '12px 24px', background: '#fafafa' }}>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <div>
                                                        <Text strong>작업 내용:</Text>
                                                        <div style={{ marginTop: 8 }}>{record.description}</div>
                                                    </div>
                                                    {record.achievement && (
                                                        <div style={{ marginTop: 8 }}>
                                                            <Text strong>작업 성과:</Text>
                                                            <div style={{ marginTop: 8 }}>{record.achievement}</div>
                                                        </div>
                                                    )}
                                                </Space>
                                            </div>
                                        ),
                                    }}
                                />
                            ),
                        },
                        {
                            key: 'approved',
                            label: `승인 완료 (${approvedLogs.length})`,
                            children: (
                                <Table
                                    columns={approvedColumns}
                                    dataSource={approvedLogs}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{ pageSize: 10 }}
                                    scroll={{ x: 1200 }}
                                    locale={{ emptyText: '승인 완료된 작업 일지가 없습니다' }}
                                    expandable={{
                                        expandedRowRender: (record) => (
                                            <div style={{ padding: '12px 24px', background: '#fafafa' }}>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <div>
                                                        <Text strong>작업 내용:</Text>
                                                        <div style={{ marginTop: 8 }}>{record.description}</div>
                                                    </div>
                                                    {record.achievement && (
                                                        <div style={{ marginTop: 8 }}>
                                                            <Text strong>작업 성과:</Text>
                                                            <div style={{ marginTop: 8 }}>{record.achievement}</div>
                                                        </div>
                                                    )}
                                                </Space>
                                            </div>
                                        ),
                                    }}
                                />
                            ),
                        },
                    ]}
                />
            </Card>

            {/* 반려 모달 */}
            <Modal
                title="작업 일지 반려"
                open={rejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setRejectModalVisible(false);
                    setRejectionReason('');
                    setSelectedLog(null);
                }}
                okText="반려"
                cancelText="취소"
                okButtonProps={{ danger: true }}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>작업자: {selectedLog?.user?.name}</Text>
                    <br />
                    <Text>작업일: {dayjs(selectedLog?.work_date).format('YYYY-MM-DD')}</Text>
                    <br />
                    <Text>작업시간: {selectedLog?.hours}시간</Text>
                </div>
                <TextArea
                    rows={4}
                    placeholder="반려 사유를 입력해주세요"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                />
            </Modal>
        </div>
    );
};

export default WorkLogApprovalPage;